/**
 * Order State Machine
 * 
 * Canonical transition map for manager-side status changes.
 * Single source of truth consumed by all UI components.
 * 
 * Transition map:
 *   New              -> Completed, Cancelled
 *   Awaiting Payment -> Completed (Mark as Paid), Cancelled
 *   Completed        -> Out for Delivery, Delivered (manual/pickup), Cancelled
 *   Out for Delivery -> Delivered, Delivery Failed
 *   Delivery Failed  -> Out for Delivery (retry), Cancelled
 *   Delivered        -> (terminal -- override only)
 *   Cancelled        -> (terminal -- override only)
 * 
 * OFD -> Cancelled is NOT a normal transition; it requires override with reason.
 */

import { ORDER_STATUS } from "@/constants/orderConstants";

/**
 * Manager-side transition map.
 * Keys are current statuses; values are arrays of allowed next statuses.
 * Terminal statuses (Delivered, Cancelled) have no entries = empty array.
 */
export const MANAGER_TRANSITIONS: Record<string, string[]> = {
  [ORDER_STATUS.NEW]: [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.AWAITING_PAYMENT]: [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.COMPLETED]: [ORDER_STATUS.OUT_FOR_DELIVERY, ORDER_STATUS.DELIVERED, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.OUT_FOR_DELIVERY]: [ORDER_STATUS.DELIVERED, ORDER_STATUS.DELIVERY_FAILED],
  [ORDER_STATUS.DELIVERY_FAILED]: [ORDER_STATUS.OUT_FOR_DELIVERY, ORDER_STATUS.CANCELLED],
};

/** Statuses that cannot be changed without an explicit override */
export const TERMINAL_STATUSES: string[] = [
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.CANCELLED,
];

/**
 * All statuses available for override (when manager explicitly overrides).
 * Excludes legacy "Processing" status.
 */
export const ALL_OVERRIDE_STATUSES: string[] = [
  ORDER_STATUS.NEW,
  ORDER_STATUS.AWAITING_PAYMENT,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.OUT_FOR_DELIVERY,
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.DELIVERY_FAILED,
  ORDER_STATUS.CANCELLED,
];

/**
 * Returns the list of valid next statuses for a given current status.
 * Returns empty array for terminal or unknown statuses.
 */
export function getNextStatuses(currentStatus: string): string[] {
  const transitions = MANAGER_TRANSITIONS[currentStatus];
  if (transitions) return transitions;

  // Terminal statuses return empty (override required)
  if (TERMINAL_STATUSES.includes(currentStatus)) return [];

  // Unknown status — warn in dev, return empty
  if (import.meta.env.DEV) {
    console.warn(`[orderStateMachine] Unknown status: "${currentStatus}". No transitions available.`);
  }
  return [];
}

/**
 * Checks if a specific transition is allowed in the normal flow.
 */
export function isTransitionAllowed(from: string, to: string): boolean {
  const allowed = MANAGER_TRANSITIONS[from];
  return !!allowed && allowed.includes(to);
}

/**
 * Computes the intersection of valid next statuses for multiple orders.
 * Used by BulkEditDialog to show only statuses valid for ALL selected orders.
 * Returns empty array if no common transitions exist.
 */
export function getCommonNextStatuses(currentStatuses: string[]): string[] {
  if (currentStatuses.length === 0) return [];

  const uniqueStatuses = [...new Set(currentStatuses)];
  const transitionSets = uniqueStatuses.map(s => getNextStatuses(s));

  // If any order has no valid transitions (terminal), intersection is empty
  if (transitionSets.some(set => set.length === 0)) return [];

  // Compute intersection
  return transitionSets[0].filter(status =>
    transitionSets.every(set => set.includes(status))
  );
}
